/**
 * KontenImport — manuelle Konten (Bank-CSV, Kasse) und Kassenbuch (Z-Bon).
 * Automatische Verbindungen (Enable Banking, PayPal) zeigt BankConnectionsPanel.
 */
import React, { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Papa from 'papaparse';
import { Landmark, Upload, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { createConnection, importTransactions, bookZBon, fmtEUR } from '@/lib/buchhaltungApi';

const PROVIDERS = [
  { value: 'bank_api', label: 'Bank', account: '1800' },
  { value: 'paypal', label: 'PayPal', account: '1810' },
  { value: 'ebay', label: 'eBay', account: '1820' },
  { value: 'cash', label: 'Kasse', account: '1600' },
];

/** CSV başlıklarını normalize eder (DE bank exportları dahil). */
function normalizeRow(row) {
  const get = (...keys) => {
    for (const k of keys) {
      const hit = Object.keys(row).find((c) => c.toLowerCase().trim() === k);
      if (hit && row[hit] !== '') return row[hit];
    }
    return null;
  };
  const rawAmount = get('amount', 'betrag', 'umsatz', 'wert');
  const rawDate = get('booking_date', 'buchungstag', 'datum', 'date', 'valutadatum');
  if (rawAmount == null || !rawDate) return null;
  // DE format: 1.234,56 → 1234.56
  const amount = parseFloat(String(rawAmount).replace(/\./g, '').replace(',', '.'))
    || parseFloat(rawAmount);
  // DE tarih: dd.mm.yyyy → yyyy-mm-dd
  let date = String(rawDate).trim();
  const m = date.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (m) date = `${m[3]}-${m[2]}-${m[1]}`;
  if (!/^\d{4}-\d{2}-\d{2}/.test(date) || !Number.isFinite(amount)) return null;
  return {
    booking_date: date.slice(0, 10),
    amount,
    purpose: get('purpose', 'verwendungszweck', 'zweck', 'beschreibung', 'description') || '',
    counterparty_name: get('counterparty_name', 'auftraggeber', 'empfänger', 'name', 'beguenstigter') || '',
    external_transaction_id: get('external_transaction_id', 'transaktions-id', 'transaction id', 'id'),
    fees: parseFloat(String(get('fees', 'gebühr', 'fee') || '0').replace(',', '.')) || 0,
  };
}

export default function KontenImport({ connections, actorEmail, onChanged }) {
  const queryClient = useQueryClient();
  const fileRef = useRef(null);
  const [selectedConnection, setSelectedConnection] = useState('');
  const [newProvider, setNewProvider] = useState('bank_api');
  const [newName, setNewName] = useState('');
  const [showNew, setShowNew] = useState(false);

  const createMutation = useMutation({
    mutationFn: () => {
      const provider = PROVIDERS.find((p) => p.value === newProvider);
      return createConnection({ provider: newProvider, displayName: newName || provider.label, linkedAccountNo: provider.account });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fibu-connections'] });
      toast.success('Konto angelegt.');
      setShowNew(false); setNewName('');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!selectedConnection) { toast.error('Bitte zuerst ein Konto wählen.'); return; }
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async ({ data }) => {
        const rows = data.map(normalizeRow).filter(Boolean);
        if (rows.length === 0) {
          toast.error('Keine gültigen Zeilen erkannt. Erwartete Spalten: Betrag/Amount + Buchungstag/Datum.');
          return;
        }
        try {
          const { imported, skipped } = await importTransactions(Number(selectedConnection), rows);
          onChanged?.();
          toast.success(`${imported} Umsatz/Umsätze importiert${skipped ? `, ${skipped} Duplikat(e) übersprungen` : ''}.`);
        } catch (err) {
          toast.error('Import-Fehler: ' + err.message);
        }
      },
      error: () => toast.error('CSV konnte nicht gelesen werden.'),
    });
    e.target.value = '';
  };

  const cash = connections.find((c) => c.provider === 'cash');

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Landmark className="w-4 h-4 text-slate-500" aria-hidden="true" /> Konten &amp; CSV-Import
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {connections.map((c) => (
              <button key={c.id} type="button" onClick={() => setSelectedConnection(String(c.id))} aria-pressed={String(c.id) === selectedConnection}
                className={cn('min-h-[44px] rounded-full border px-3 text-xs font-semibold',
                  String(c.id) === selectedConnection ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50')}>
                {c.display_name} · {c.linked_account_no}
              </button>
            ))}
            <Button variant="outline" size="sm" className="h-11 gap-1 text-xs" onClick={() => setShowNew(!showNew)}>
              <Plus className="w-3.5 h-3.5" /> Konto
            </Button>
          </div>

          {showNew && (
            <div className="flex flex-col sm:flex-row gap-2 p-3 bg-slate-50 rounded-lg border">
              <Select value={newProvider} onValueChange={setNewProvider}>
                <SelectTrigger className="h-11 sm:w-36 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROVIDERS.map((p) => (
                    <SelectItem key={p.value} value={p.value} className="text-xs">{p.label} → {p.account}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input placeholder="Anzeigename (z. B. Sparkasse)" value={newName} onChange={(e) => setNewName(e.target.value)} className="h-11 text-xs" />
              <Button className="h-11 bg-slate-900 text-white text-xs font-bold" disabled={createMutation.isPending} onClick={() => createMutation.mutate()}>Anlegen</Button>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />
            <Button variant="outline" className="h-11 gap-2 text-xs font-bold" disabled={!selectedConnection} onClick={() => fileRef.current?.click()}>
              <Upload className="w-4 h-4" /> CSV importieren
            </Button>
            <p className="text-xs text-slate-500">{selectedConnection ? 'Kontoauszug/PayPal-Export als CSV.' : 'Zuerst Konto wählen.'}</p>
          </div>
        </CardContent>
      </Card>
      {cash && <ZBonCard connection={cash} actorEmail={actorEmail} onBooked={onChanged} />}
    </div>
  );
}

/**
 * Z-Bon (Tagesabschluss) girişi — Kasse an Erlöse 19%/7% + USt otomatik
 * hesaplanıp buchulur. Gün başına bir kez (DB dedupe).
 */
function ZBonCard({ connection, actorEmail, onBooked }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [gross19, setGross19] = useState('');
  const [gross7, setGross7] = useState('');
  const [pending, setPending] = useState(false);
  const total = (parseFloat(gross19) || 0) + (parseFloat(gross7) || 0);

  const submit = async () => {
    setPending(true);
    try {
      await bookZBon({
        connectionId: connection.id, date,
        gross19: parseFloat(gross19) || 0,
        gross7: parseFloat(gross7) || 0,
        actorEmail,
      });
      toast.success(`Z-Bon ${date} gebucht: Kasse an Erlöse (${fmtEUR(total)}).`);
      setGross19(''); setGross7('');
      onBooked?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPending(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Kassenbuch — Z-Bon erfassen</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-500">Datum</label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11 text-xs" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-500">Brutto 19 %</label>
          <Input type="number" inputMode="decimal" placeholder="0,00" value={gross19}
            onChange={(e) => setGross19(e.target.value)} className="h-11 text-xs text-right" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-500">Brutto 7 %</label>
          <Input type="number" inputMode="decimal" placeholder="0,00" value={gross7}
            onChange={(e) => setGross7(e.target.value)} className="h-11 text-xs text-right" />
        </div>
        <Button
          className="h-11 bg-slate-900 hover:bg-slate-800 text-xs font-bold col-span-2 sm:col-span-1"
          disabled={total <= 0 || pending}
          onClick={submit}
        >
          {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : `Buchen ${total > 0 ? fmtEUR(total) : ''}`}
        </Button>
      </CardContent>
    </Card>
  );
}
